import i18n from 'i18next'
import { initReactI18next, useTranslation } from 'react-i18next'

import { type resources as Resources } from 'locales/da/translations.d'
import daTranslation from 'locales/da/translations.json'
import esTranslation from 'locales/es/translations.json'
import frTranslation from 'locales/fr/translations.json'
import nnTranslation from 'locales/nn/translations.json'
import noTranslation from 'locales/no/translations.json'
import svTranslation from 'locales/sv/translations.json'
import { Language } from 'types/Api'
import { rollbar } from 'utils/rollbar'

import { getConfiguration } from './config'

const languages: Language[] = ['da', 'es', 'fr', 'sv', 'no', 'nn']

const resources: Record<Language, { translation: Record<string, string | object> }> = {
  da: { translation: daTranslation },
  es: { translation: esTranslation },
  fr: { translation: frTranslation },
  nn: { translation: nnTranslation },
  no: { translation: noTranslation },
  sv: { translation: svTranslation },
}

declare module 'i18next' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface CustomTypeOptions {
    resources: {
      translation: typeof Resources
    }
  }
}

i18n.use(initReactI18next).init({
  debug: import.meta.env.MODE === 'development',
  fallbackLng: getConfiguration().languageCode,
  supportedLngs: languages,
  interpolation: {
    escapeValue: false,
    format: (value, format, lng) => {
      if (value instanceof Date && format === 'date') {
        return new Intl.DateTimeFormat(lng, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(value)
      }

      return value
    },
  },
  missingKeyHandler: (lngs, ns, key) => {
    rollbar.warning(
      `Translation error: missing translation for ${ns}:${key}, languages are ${lngs.toString()}`,
    )
  },
  resources,
})

const changeLanguage = (language: Language | 'nn') => {
  if (language === i18n.language) {
    return
  }
  if (!languages.includes(language)) {
    rollbar.warning(`Attempted to set language to unsupported language: ${language}`)
    return
  }
  i18n.changeLanguage(language)
}

// eslint-disable-next-line import-x/no-default-export
export default i18n

export { useTranslation, changeLanguage }
